import { Divider, HStack, Stack, Text, VStack } from 'native-base';
import Button from 'ui/components/Button';
import DataTable from 'ui/components/DataTable';
import { TableSizeClass } from 'ui/components/DataTable/@types/basicTypes';
import Icon from 'ui/components/Icon';
import { GenericFormTabs } from 'ui/enums';
import { ModalInformation } from 'ui/components/ModalInformation';
import Modal from 'ui/components/Modals/Modal';

import ModalDelete from '~/components/ModalDelete';
import { colors } from '~/theme/colors';

import { ActivityStatusModalProvider } from './context/ActivityStatusModalContext';
import { useLocationController } from './controller';
import { columnsByActivityType, defaultFilterValues } from './tableConfig';
import { LocationSectionProps } from './types';

export const LocationSection = ({
  activityType,
  loadActivitiesList,
  handleEditActivity,
  isOrganizationOwnerOfActivity,
  locationOption,
}: LocationSectionProps) => {
  const {
    isLoadingChangeCloseApplication,
    changeApplicationModalRef,
    handleCancelCloseApplication,
    handleChangeApplicationStatus,
    refetchData,
    closeApplicationData,
    renderLocationsTableDescription,
    changeActivityStatusModalRef,
    activityStatusModalInformationRef,
    handleOpenChangeStatusModal,
    handleCloseChangeStatusModal,
    handleConfirmChangeStatus,
    handleOpenModalStatusInformation,
    changeStatusData,
  } = useLocationController({ activityType, locationOption });

  const modalConfigByStatus = {
    ARCHIVED: {
      title: 'Archive activity',
      text: 'Do you really want to archive this activity? You can unarchive it later if you change your mind.',
      buttonText: 'Archive activity',
      buttonColor: colors.error['600'],
    },
    CLOSED: {
      title: 'Close activity',
      text: 'Do you really want to close this activity? You can reopen it later if you change your mind.',
      buttonText: 'Close activity',
      buttonColor: colors.error['600'],
    },
    OPEN: {
      title: 'Open activity',
      text: 'Do you really want to open this activity? You can archive or close it later if you change your mind.',
      buttonText: 'Open activity',
      buttonColor: colors.primary['500'],
    },
  };

  const modalConfig = modalConfigByStatus[changeStatusData.newStatus] || {
    title: 'Change status',
    text: 'Do you really want to change this activity status?',
    buttonText: 'Yes, change',
    buttonColor: colors.primary['500'],
  };

  return (
    <ActivityStatusModalProvider
      openModal={handleOpenChangeStatusModal}
      closeModal={handleCloseChangeStatusModal}
    >
      <Stack>
        <HStack justifyContent="space-between" alignItems="center" mb={2}>
          <VStack space={2}>
            <HStack alignItems="center" space={2}>
              <Icon
                icon={'map-pin'}
                color={colors.gray[900]}
                alt="map pin icon"
                size={20}
              />
              <Text color="gray.900" fontSize="2xl" fontWeight={500}>
                Location
              </Text>
            </HStack>
            <Text color="gray.900" fontSize="sm" fontWeight={400}>
              {renderLocationsTableDescription()}
            </Text>
          </VStack>
          {isOrganizationOwnerOfActivity ? (
            // <Permissions.ValidateApp
            //   appIdToValidate={appId}
            //   requiredPermissions={activityPermissions}
            //   disablePermissionsValidation={isAppAdmin}
            // >
            <Button
              w="76px"
              borderRadius={12}
              py={2}
              px={3}
              minW="auto"
              leftIconName="edit"
              alignItems="center"
              leftIconColor={colors.primary[400]}
              variant="outline"
              onPress={() => handleEditActivity(GenericFormTabs.WhenWhere)}
            >
              <Text color={colors.primary[400]}>Edit</Text>
            </Button>
          ) : // </Permissions.ValidateApp>
          null}
        </HStack>
        <Divider mb={6} />
        <DataTable
          refetchQuantity={refetchData}
          columns={columnsByActivityType(
            activityType,
            locationOption,
            handleOpenModalStatusInformation,
          )}
          defaultFilterValues={defaultFilterValues}
          loadData={loadActivitiesList as any}
          defaultLimit={5}
          pageSizes={[5]}
          className={TableSizeClass.Small}
          limit={5}
        />
      </Stack>
      <ModalDelete
        modalRef={changeApplicationModalRef}
        headerText="Close applications"
        rightBtnText="Yes, close"
        handleCloseModal={handleCancelCloseApplication}
        // Keep with arrow function to not forward the events
        handleDelete={() =>
          handleChangeApplicationStatus(
            closeApplicationData.id,
            closeApplicationData.isToggled,
            closeApplicationData.endDate,
          )
        }
        rightBtnLoading={isLoadingChangeCloseApplication}
      >
        <Stack space="5">
          <Text>Are you sure that you want to close this activity?</Text>
          <Text>The activity will immediately close for new applications.</Text>
        </Stack>
      </ModalDelete>

      <Modal
        headerText={modalConfig.title}
        ref={changeActivityStatusModalRef}
        leftBtnAction={handleCloseChangeStatusModal}
        rightBtnAction={handleConfirmChangeStatus}
        rightBtnText={modalConfig.buttonText}
        leftBtnText="No, go back"
        rightBtnLoading={isLoadingChangeCloseApplication}
        modalContentMaxW="669"
        rightBtnBgColor={modalConfig.buttonColor}
      >
        <Stack px={4} pt={4} space="5">
          <Text fontSize="md" color="gray.500">
            {modalConfig.text}
          </Text>
        </Stack>
      </Modal>
      <ModalInformation
        headerText="Activity Status"
        modalRef={activityStatusModalInformationRef}
      >
        <VStack pt={6} space={4}>
          <Text fontSize="md" color="gray.500">
            The activity status indicates whether the opportunity is{' '}
            <strong>OPEN</strong> for applications, <strong>CLOSED</strong>, or{' '}
            <strong>ARCHIVED</strong>. Activities are initially published with
            an open status. Each location associated with it have its own
            individual status, and the administrator has the ability to change
            that.
          </Text>
          <Text fontSize="md" color="gray.500">
            If a position has been filled or if you no longer wish to accept
            applications, you can click on{' '}
            <strong>Options &gt; Change Status &gt; Closed</strong> to stop
            receiving applications. Alternatively, you can change the status to
            &quot;Archived&quot;. This feature is particularly useful for
            seasonal opportunities that are offered periodically.
          </Text>
        </VStack>
      </ModalInformation>
    </ActivityStatusModalProvider>
  );
};
